import { Duration, Moment } from "moment";
import { getDefaultTimePeriodIndex, timePeriodDefinitions } from "../components/search/facets/timeFacetLogic";
import { DefaultFieldNames, Field } from "./Field";
import { DURATION_MAXIMUM_VALUE, DURATION_MINIMUM_VALUE } from "../components/search/facets/durationFacetLogic";
import { toDisplayString } from "../logic/displayString";

export interface FacetValues<T extends FacetValue> {    
    facetName: string;
    facetValues: T[];
}

export interface FacetValuesCount {
    facetName: string;
    facetValues: FacetValueCount<FacetValue>[];
}


/**
 * Represents the base structure for a facet value, providing methods to retrieve
 * its string and display representations.
 *
 * @property facetName - The name of the facet this value belongs to.
 * @method getStringValue - Returns the string representation of the facet value.
 * @method getDisplayValue - Returns a user-friendly display string for the facet value. It can include formatting or transformation logic.
 *                          For example, it can convert values like "audio,video,screen" to "Audio, Video, Screen".

 */
export interface FacetValueBase {                  
    facetName: string
    getStringValue(): string;
    getDisplayValue(): string;
}

export interface FacetValueCount<T extends FacetValueBase> {          
    count: number;    
    facetValue: T;
}

export class StringFacetValue implements FacetValueBase {
    constructor(public facetName: string, public value: string) {
        this.displayValue = null;
    }

    private displayValue: string | null;
    
    getStringValue(): string {
        return this.value;
    }
    
    getDisplayValue(): string {
        switch (this.facetName) {
            case DefaultFieldNames.MediaTypes:
            case DefaultFieldNames.SourceType:
            case DefaultFieldNames.Direction:
            case DefaultFieldNames.ParticipantsType:
                if (this.displayValue) {
                    return this.displayValue;
                }

                this.displayValue = toDisplayString(this.value) as string;
                return this.displayValue;
            default:            
                return this.getStringValue();
        }
    }
}

export class NumberFacetValue implements FacetValueBase {
    constructor(public facetName: string, public value: number) {}

    getStringValue(): string {
        return this.value.toString();
    }

    getDisplayValue(): string {
        return this.value.toString();
    }
}

export class DurationFacetValue implements FacetValueBase {
    constructor(public facetName: string, public periodIndex: number, public lowerValue: number, public upperValue: number) {}

    getStringValue(): string {
        return `${this.lowerValue} - ${this.upperValue} seconds`;
    }

    getDisplayValue(): string {
        return this.getStringValue();
    }
}

/**
 * Represents a facet value for a time range, typically used for filtering or categorization.
 * 
 * @implements {FacetValueBase}
 * 
 * @param periodIndex - The index of the period this facet value represents, or null if period is a custom date range (it would be nice to refactor to an specific index, because now it's confusing at some places)
 * @param period - 
 * @param fromDateTime - 
 * @param toDateTime - 
 * 
 * @method getStringValue - Returns a string representation of the time range in the format "lowerValue - upperValue".
 */
export class TimeStartFacetValue implements FacetValueBase {
    constructor(public facetName: string, public periodIndex: number | null, public period: Duration, public fromDateTime: Moment | null, public toDateTime: Moment | null) {}

    getStringValue(): string {
        if (this.periodIndex !== null) {
            return timePeriodDefinitions[this.periodIndex].displayName;
        }

        return `Custom`;
    }

    getDisplayValue(): string {
        return this.getStringValue();
    }    
}

export class GeographyPointsFacetValue implements FacetValueBase {
    constructor(public facetName: string, public longitude: number, public latitude: number, public radius: number) {}

    getStringValue(): string {
        return `${this.longitude}, ${this.latitude} (radius: ${this.radius})`;
    }

    getDisplayValue(): string {
        return this.getStringValue();
    }    
}

export type FacetValue =
  | StringFacetValue
  | NumberFacetValue
  | DurationFacetValue
  | TimeStartFacetValue
  | GeographyPointsFacetValue

export interface ReplayAPI_FacetValueBase {          
    count: number;    
}

export interface ReplayAPI_StringFacetValue extends ReplayAPI_FacetValueBase {          
    value : string;
}

export interface ReplayAPI_NumberFacetValue extends ReplayAPI_FacetValueBase {          
    value : number;
}

export interface ReplayAPI_DurationFacetValue extends ReplayAPI_FacetValueBase {    
    displayName: string;
    periodIndex: number;
    lowerValue: number;
    upperValue: number;
    checked: boolean;
}


export interface ReplayAPI_TimeStartFacetValue extends ReplayAPI_FacetValueBase {
    type: string;
    from: string;
    to: string;
}

export interface ReplayAPI_GeographyPointsFacetValue extends ReplayAPI_FacetValueBase {
    longitude: number;
    latitude: number;
    radius: number;
}

export type ReplayAPI_FacetValue =
  | ReplayAPI_StringFacetValue
  | ReplayAPI_NumberFacetValue
  | ReplayAPI_DurationFacetValue
  | ReplayAPI_TimeStartFacetValue
  | ReplayAPI_GeographyPointsFacetValue

export function isDefaultFacetFilter(field: Field, facetValues: FacetValues<FacetValue>): boolean {
    switch (field.name) {
        case DefaultFieldNames.TimeStart:                        
            if (field.name === DefaultFieldNames.TimeStart) {
                return (
                    facetValues.facetValues.length === 1 &&
                    facetValues.facetValues[0] instanceof TimeStartFacetValue &&
                    facetValues.facetValues[0].periodIndex === getDefaultTimePeriodIndex()
                );
            }
            return false;
        case DefaultFieldNames.Duration:            
            if (field.name === DefaultFieldNames.Duration) {
                return (
                    facetValues.facetValues.length === 1 &&
                    facetValues.facetValues[0] instanceof DurationFacetValue &&
                    facetValues.facetValues[0].lowerValue === DURATION_MINIMUM_VALUE &&
                    facetValues.facetValues[0].upperValue === DURATION_MAXIMUM_VALUE 
                );
            }
    }

    return facetValues.facetValues.length === 0;
}